// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { isSameStoreId } from '@iheartjane/cart/utils';
import type {
  BrandSpecial,
  ReservationCartProduct,
} from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { getMaxCartQuantity } from '@iheartjane/menu';
import { Typography } from '@iheartjane/reefer';
import type {
  AppMode,
  Breadcrumb,
  Nullable,
  PriceId,
  ProductThreshold,
} from '@iheartjane/types';
import { useEffect, useRef, useState } from 'react';
import type { DeepReadonly } from 'ts-essentials';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, Store, StoreSpecial } from '@jane/shared/models';
import type {
  Brand,
  EcommPaths,
  UserLocation,
  MenuProduct as ZMenuProduct,
} from '@jane/shared/types';

import type {
  AddData,
  DeleteData,
  ProductCardContextProps,
} from '../productCardProvider';
import { ProductCardProvider } from '../productCardProvider';
import { SponsoredTag } from '../sponsoredTag/sponsoredTag';
import { ListViewProductCard } from './listView/listViewProductCard';
import { MenuProductCardContainer } from './menuProductCardContainer';
import { ItemContainer, sortByPriceId } from './utils/productCardHelper';

/**
 * Structural so both the v1 and the v3 cart product satisfy it — the card only
 * ever matches on `id` and reads `price_id`/`count` off the matches.
 */
interface MenuCardCartProduct {
  count: number;
  id: number | string;
  price_id: PriceId;
}

/**
 * Both ids are string-normalised: `Store['id']` is `number | string` while the
 * v3 cart's is always a number, so `===` drops the match for a string-id store.
 */
const findCartProductForMenuProduct = (
  cartProducts: readonly MenuCardCartProduct[],
  menuProductId: string | number,
  storeId: string | number,
  cartStoreId?: string | number
): ReservationCartProduct[] => {
  const cartProductForMenuProduct = cartProducts.filter(
    (cp) =>
      String(cp.id) === String(menuProductId) &&
      isSameStoreId(cartStoreId, storeId)
  ) as unknown as ReservationCartProduct[];

  return cartProductForMenuProduct;
};

const MenuItemContainer = styled.div<{ disabledState?: boolean }>(
  {
    flex: '0 0 auto',
    position: 'relative',
    width: '100%',
  },
  ({ disabledState }) => {
    return [
      {
        cursor: 'default',
        opacity: disabledState ? 0.5 : 1,
      },
    ];
  }
);

export interface LegacyMenuProductCardProps {
  appliedWeightFilter?: Nullable<PriceId>;
  appMode: AppMode;
  brand?: Brand;
  /** For rendering the jane gold tag */
  brandSpecials?: BrandSpecial[];
  breadcrumbs?: Breadcrumb[];
  carouselView?: boolean;
  cartIsOpen: boolean;
  cartProducts?: readonly MenuCardCartProduct[];
  cartStoreId?: string | number;
  compareOverlay?: React.ReactNode;
  currentSpecial?: StoreSpecial;
  customerId?: number | null;
  /** Makes the component transparent, to indicate a product or row is toggled off */
  disabledState?: boolean;
  /** Disables any click actions (and tracking events) on the card and any sub-components */
  disableInteraction?: boolean;
  disableWeightFilter?: boolean;
  fromAllSpecials?: boolean | null;
  fromSpecialId?: string | number | null;
  hideActions?: boolean;
  isMenuTopRow?: boolean;
  /**
   * Boolean that determines if all the products in the smart instance
   * are sponsored
   */
  isRowSponsored?: boolean;
  itemWidth?: number | null;
  /** Determined in ProductCardWithTracking so we can track Jane Gold deals */
  janeGoldLabel?: string | null;
  listView: boolean;
  maxWeightOption?: number | null;
  menuRowRules?: {
    product_threshold?: ProductThreshold | null;
  };
  onAddToCart?: (addData: AddData) => void;
  onClick?: (selectedWeight: PriceId | null) => void;
  onDeleteFromCart?: (deleteData: DeleteData) => void;
  pdpPath?: EcommPaths['menuProduct'];
  product: MenuProduct | ZMenuProduct;
  productInstance?: SmartSortProduct<AlgoliaProduct>;
  searchState?: JaneSearchState<AlgoliaProduct>;
  setProductCardWidth?: (arg: number) => void;
  showOnlyWeights?: string[] | null;
  store: DeepReadonly<Store>;
  userLocation: UserLocation;
  weightFilter?: (
    weight: string,
    product: ZMenuProduct | AlgoliaProduct
  ) => boolean;
}

export const LegacyMenuProductCard = ({
  appMode,
  appliedWeightFilter,
  brand,
  brandSpecials,
  breadcrumbs,
  carouselView,
  cartIsOpen,
  cartProducts = [],
  cartStoreId,
  compareOverlay,
  currentSpecial,
  customerId,
  disabledState,
  disableInteraction,
  disableWeightFilter,
  fromAllSpecials,
  hideActions,
  itemWidth,
  janeGoldLabel,
  listView,
  onClick,
  onAddToCart,
  onDeleteFromCart,
  pdpPath,
  product,
  searchState,
  showOnlyWeights,
  setProductCardWidth,
  store,
  fromSpecialId,
  maxWeightOption,
  userLocation,
  productInstance,
  isRowSponsored = false,
  isMenuTopRow = false,
  menuRowRules,
  weightFilter,
}: LegacyMenuProductCardProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedWeight, setSelectedWeight] = useState<PriceId>('each');

  const inventoryCount =
    !!product && !!selectedWeight
      ? getMaxCartQuantity(product, selectedWeight)
      : undefined;

  const showInventoryCount = !!inventoryCount && inventoryCount < 4;

  useEffect(() => {
    setProductCardWidth &&
      containerRef.current &&
      setProductCardWidth(
        itemWidth || containerRef.current.getBoundingClientRect().width
      );
  }, []);

  const cartProduct = sortByPriceId(
    findCartProductForMenuProduct(
      cartProducts,
      product.id,
      store.id,
      cartStoreId
    ),
    product
  );

  const productCardContext: Partial<ProductCardContextProps> = {
    appMode,
    appliedWeightFilter,
    brand,
    brandSpecials,
    breadcrumbs,
    carouselView,
    cartIsOpen,
    cartProduct,
    compareOverlay,
    currentSpecial,
    customerId,
    disableInteraction,
    disableWeightFilter,
    hideActions,
    janeGoldLabel,
    listView,
    onAddToCart,
    onDeleteFromCart,
    pdpPath,
    product: product,
    menuProduct: product as MenuProduct,
    showOnlyWeights,
    searchState,
    selectedWeight,
    setSelectedWeight,
    store,
    trackListViewClick: onClick
      ? () => {
          onClick(selectedWeight);
        }
      : undefined,
    fromSpecialId,
    fromAllSpecials,
    maxWeightOption,
    userLocation,
    menuRowRules,
    weightFilter,
  };

  /**
   * @todo Remove this when legacy code is deleted since product
   * instance will always be available
   */
  const shouldDisplaySponsoredTag = productInstance
    ? isMenuTopRow || isRowSponsored
      ? false
      : productInstance.isSponsored
    : false;

  if (listView) {
    return (
      <ProductCardProvider value={productCardContext}>
        <ListViewProductCard
          data-product-id={product.id}
          isAd={shouldDisplaySponsoredTag}
        />
      </ProductCardProvider>
    );
  }

  return (
    <ProductCardProvider value={productCardContext}>
      <ItemContainer
        data-testid={`product-card-container-${product.id}`}
        ref={containerRef}
      >
        <MenuItemContainer
          data-testid="product-card"
          data-product-id={product.id}
          disabledState={disabledState}
          onClick={
            onClick
              ? () => {
                  onClick(selectedWeight);
                }
              : undefined
          }
        >
          <MenuProductCardContainer
            shouldDisplaySponsoredTag={shouldDisplaySponsoredTag}
          />
        </MenuItemContainer>
        {showInventoryCount && appMode !== 'bloom' && (
          <Typography textAlign="center" variant="small-body-regular" mt={8}>
            Only {inventoryCount} left
          </Typography>
        )}
        {appMode !== 'bloom' && (
          <SponsoredTag hideText={!shouldDisplaySponsoredTag} mt={8} />
        )}
      </ItemContainer>
    </ProductCardProvider>
  );
};
